import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonService } from 'src/app/_services/common.service';
import { environment } from 'src/environments/environment';

// Rejects free mail providers so we only collect business emails
const FREE_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'aol.com', 'msn.com', 'proton.me', 'protonmail.com'];

const COUNTRY_CODES = [
  'SA', 'AE', 'KW', 'QA', 'BH', 'OM', 'JO', 'EG', 'IQ', 'LB', 'SY', 'PS', 'YE', 'LY', 'TN', 'DZ', 'MA', 'SD',
  'TR', 'PK', 'IN', 'GB', 'US', 'CA', 'DE', 'FR', 'IT', 'ES', 'NL', 'AU', 'MY', 'ID', 'SG', 'CN', 'JP', 'KR',
];

@Component({
  selector: 'app-awareness-posters',
  templateUrl: './awareness-posters.component.html',
  styleUrls: ['./awareness-posters.component.scss']
})
export class AwarenessPostersComponent implements OnInit {

  currentLanguage = this.commonService.currentLanguage;
  posterBundles: { [language: string]: string } = {
    english: 'SolidRange-Awareness-Posters-English.zip',
    arabic: 'SolidRange-Awareness-Posters-Arabic.zip',
    both: 'SolidRange-Awareness-Posters-English-Arabic.zip',
  };
  countries: { code: string; name: string }[] = [];
  submitted = false;
  downloaded = false;
  isSubmitting = false;

  leadForm: FormGroup = this.fb.group({
    interestedInDemo: [true],
    name: ['', [Validators.required, Validators.maxLength(100)]],
    // Optional, only validated when filled in
    mobileNumber: ['', Validators.pattern(/^\+?[0-9\s-]{7,15}$/)],
    businessEmail: ['', [Validators.required, Validators.email, this.businessEmailValidator]],
    country: ['SA', Validators.required],
    posterLanguage: ['', Validators.required],
    // Honeypot: hidden from people, bots that fill it are not saved
    website: [''],
  });

  constructor(private fb: FormBuilder, private commonService: CommonService) { }

  get bundleFileName(): string {
    return this.posterBundles[this.leadForm.get('posterLanguage')?.value] || '';
  }

  get bundleUrl(): string {
    return `assets/files/${this.bundleFileName}`;
  }

  ngOnInit(): void {
    const displayNames = new Intl.DisplayNames([this.currentLanguage], { type: 'region' });
    this.countries = COUNTRY_CODES
      .map(code => ({ code, name: displayNames.of(code) || code }))
      .sort((a, b) => a.name.localeCompare(b.name, this.currentLanguage));
  }

  businessEmailValidator(control: { value: string }) {
    const domain = (control.value || '').split('@')[1]?.toLowerCase();
    return domain && FREE_EMAIL_DOMAINS.includes(domain) ? { freeEmail: true } : null;
  }

  isInvalid(name: string): boolean {
    const control = this.leadForm.get(name);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  hasError(name: string, error: string): boolean {
    return !!this.leadForm.get(name)?.hasError(error);
  }

  onSubmit() {
    this.submitted = true;
    if (this.leadForm.invalid) {
      this.leadForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.saveLead();
    this.downloadBundle();
    this.downloaded = true;
    this.isSubmitting = false;
  }

  // Sends the lead to the Power Automate flow, which adds a row to the Excel sheet.
  // Fire-and-forget so the download never waits on or fails because of it.
  saveLead() {
    const value = this.leadForm.getRawValue();
    if (!environment.leadsFlowUrl || value.website) {
      return;
    }
    const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const lead = {
      submittedAt: new Date().toISOString(),
      interestedInDemo: value.interestedInDemo ? 'Yes' : 'No',
      // Sent as firstName to match the existing "First Name" column in the Excel sheet
      firstName: value.name.trim(),
      mobileNumber: value.mobileNumber.trim(),
      businessEmail: value.businessEmail.trim(),
      country: value.country ? countryNames.of(value.country) : '',
      postersLanguage: value.posterLanguage,
      siteLanguage: this.currentLanguage,
    };
    fetch(environment.leadsFlowUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lead),
      keepalive: true,
    }).catch(error => console.error('Failed to save lead', error));
  }

  downloadBundle() {
    const link = document.createElement('a');
    link.href = this.bundleUrl;
    link.download = this.bundleFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
